import { PracticePatientForm } from "@physiofit-digital/physiofit_types";
import * as Sentry from "@sentry/react";
import { arrayUnion, doc, setDoc, updateDoc } from "firebase/firestore";
import {
  getCurrentFormType,
  getCurrentPracticePatientForm,
  getPracticePatientId,
  getWebAppScope,
} from "../../../core/store/currentFormSlice";
import { useMainStore } from "../../../core/store/mainStore";
import { getUuid } from "../../../core/utils/helpers";
import { db } from "../../../firebaseConfig";
import { formFlagChanges } from "./formFlagChanges";

export const uploadForm = async (): Promise<boolean> => {
  const state = useMainStore.getState();
  const practicePatientFormData = getCurrentPracticePatientForm(state);
  const practicePatientId = getPracticePatientId(state);
  const webAppScope = getWebAppScope(state);
  const formType = getCurrentFormType(state);

  // The rules require both to equal the session token's claims, so the form's own practice and chain
  // are always the ones the server derived at login, never the URL (PF-6132).
  if (!webAppScope) {
    Sentry.captureException(new Error("uploadForm: web app scope is missing"));
    return false;
  }
  const { practiceId, chainId } = webAppScope;

  if (
    !practicePatientFormData ||
    !practicePatientId ||
    !practiceId ||
    !chainId
  ) {
    console.error(
      "Missing required data to upload form:",
      !practicePatientFormData ? "userForm is missing" : "",
      !practicePatientId ? "practicePatientId is missing" : "",
      !practiceId ? "practiceId is missing" : "",
      !chainId ? "chainId is missing" : "",
    );
    return false;
  }

  const filteredQuestions = Object.fromEntries(
    Object.entries(practicePatientFormData.questions).filter(
      ([, question]) => question.questionIndex !== -1,
    ),
  );

  const userFormData: PracticePatientForm = {
    id: getUuid(),
    name: practicePatientFormData?.name || "",
    patientId: practicePatientId || "",
    practiceId: practiceId || "",
    chainId: chainId || "",
    createdAt: new Date().toISOString(),
    formPatientData: {
      ...practicePatientFormData,
      questions: filteredQuestions,
    },
  };

  try {
    const updatePracticePatient = async () => {
      const changeData: Record<string, unknown> = {
        lastActivityAt: new Date().toISOString(),
        ...formFlagChanges(formType),
      };
      if (formType === "onboarding") {
        changeData["admissionInfo.lastAdmissionAt"] = new Date().toISOString();
        changeData["admissionInfo.submittedForms"] = arrayUnion({
          formId: userFormData?.id,
          formName: practicePatientFormData?.name || "",
          submittedAt: new Date().toISOString(),
        });
      }
      if (formType === "anamnesis") {
        changeData["anamnesisInfo.lastAnamnesisAt"] = new Date().toISOString();
        changeData["anamnesisInfo.submittedForms"] = arrayUnion({
          formId: userFormData?.id,
          formName: practicePatientFormData?.name || "",
          submittedAt: new Date().toISOString(),
        });
      }
      if (formType === "other") {
        changeData["otherFormsInfo.lastFormSubmissionAt"] =
          new Date().toISOString();
        changeData["otherFormsInfo.submittedForms"] = arrayUnion({
          formId: userFormData?.id,
          formName: practicePatientFormData?.name || "",
          submittedAt: new Date().toISOString(),
        });
      }
      await updateDoc(doc(db, "practicePatients/" + practicePatientId), {
        ...changeData,
      });
    };

    const writeFormData = async () => {
      await setDoc(doc(db, "patientForms", userFormData.id), userFormData);
    };

    await Promise.all([updatePracticePatient(), writeFormData()]);
    return true;
  } catch (error) {
    console.error("Error uploading form data:", error);
    Sentry.captureException(error);
    return false;
  }
};
